import type { HeroPhoto, HeroVideo } from './media.ts'
import { agencyList } from './agencies/index.ts'
import { americaTheme, deriveTheme, type MissionPalette, type ThemePair } from './theme.ts'

export const motifNames = [
  'eagle',
  'star',
  'atom',
  'bison',
  'wheat',
  'lighthouse',
  'scales',
  'key',
  'shield',
  'globe',
  'house',
  'oak',
  'sun',
  'swirl',
  'orbit',
  'flower',
  'anvil',
  'capitol',
  'column',
  'envelope',
  'book',
  'torch',
  'wave',
  'mountain',
  'building',
  'chart',
  'hands',
  'bolt',
  'anchor',
  'plane',
  'dove',
  'scroll',
  'pine',
  'hammer',
  'delta',
  'road',
  'car',
  'helix',
  'flask',
  'bulb',
] as const

export type MotifName = (typeof motifNames)[number]

export type Cited = { text: string; url: string }

/** Destinations for the identifier's required links, as the agency publishes them. */
export type IdentifierLinks = Partial<
  Record<'about' | 'accessibility' | 'foia' | 'noFear' | 'oig' | 'performance' | 'privacy', string>
>

export type Mission = {
  id: string
  name: string
  short: string
  /**
   * The word the site uses for the mission in "Design for Agriculture." and
   * "Agriculture Design System": one word where one fits.
   */
  call?: string
  kind: 'nation' | 'department' | 'independent' | 'subagency'
  /** For a sub-agency, the id of its department, or of the agency an office belongs to. */
  parent?: string
  /** Lists the mission with the Intelligence Community or the financial regulators in the mission picker. */
  community?: 'intelligence' | 'financial'
  url: string
  established?: number
  palette: MissionPalette
  colorSource: { kind: 'brand-guide' | 'site' | 'seal' | 'flag'; url: string }
  motif: MotifName
  mission: Cited
  fact?: Cited
  /** Path under public/, and the Wikimedia Commons page it came from. */
  seal?: { file: string; source: string }
  /** The agency logo where it differs from the seal. */
  logo?: { file: string; source: string }
  /** The agency's own pages for the identifier's required links. */
  identifier?: IdentifierLinks
  /** Hero media for this mission; the America set is used when absent. */
  media?: { photos?: HeroPhoto[]; video?: HeroVideo }
}

export const america: Mission = {
  id: 'america',
  name: 'United States of America',
  short: 'USA',
  kind: 'nation',
  url: 'https://america.gov/',
  established: 1776,
  palette: { primary: '#002664', secondary: '#000c1f', accent: '#d80007' },
  colorSource: { kind: 'site', url: 'https://america.gov/' },
  motif: 'star',
  seal: {
    file: 'public/seals/america.png',
    source: 'https://commons.wikimedia.org/wiki/File:Great_Seal_of_the_United_States_(obverse).svg',
  },
  mission: {
    text: 'We hold these truths to be self-evident, that all men are created equal.',
    url: 'https://www.archives.gov/founding-docs/declaration-transcript',
  },
}

export const agencies: Mission[] = agencyList

export const missions: Mission[] = [america, ...agencies]

/**
 * The address of a mission's home page: its abbreviation, lowercased, such as
 * /cbp or /dow. The nation's is the site root.
 */
export function missionPath(mission: Mission): string {
  if (mission.id === america.id) return '/'
  return `/${mission.short.toLowerCase().replace(/[&.]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`
}

/** The mission whose home page lives at a path, if one does. */
export function missionAt(pathname: string): Mission | undefined {
  return missions.find((mission) => missionPath(mission) === pathname)
}

export function findMission(id: string | null | undefined): Mission | undefined {
  return missions.find((mission) => mission.id === id)
}

/** A sub-agency's department, if it has one. */
export function parentOf(mission: Mission): Mission | undefined {
  return mission.parent ? findMission(mission.parent) : undefined
}

/** The sub-agencies under a department, in list order. */
/** The departments and agencies a mission sits within, nearest first. */
export function ancestorsOf(mission: Mission): Mission[] {
  const parent = parentOf(mission)
  return parent ? [parent, ...ancestorsOf(parent)] : []
}

export function childrenOf(mission: Mission): Mission[] {
  return missions.filter((candidate) => candidate.parent === mission.id)
}

/** Hero video for a mission: its own, then its department's, then the America loop. */
export function videoFor(mission: Mission, fallback: HeroVideo | null): HeroVideo | null {
  return mission.media?.video ?? parentOf(mission)?.media?.video ?? fallback
}

/**
 * What a mission is called in the wordmark and the headline: a department by
 * its subject ("War", "Homeland Security"), anything else by its common name
 * ("Coast Guard") or, when that runs long, its acronym ("FEMA").
 */
export function callName(mission: Mission): string {
  if (mission.id === america.id) return 'America'
  if (mission.call) return mission.call
  const subject = mission.name.match(/Department of (?:the )?(.+)$/)?.[1]
  if (subject) return subject.length <= 18 ? subject : mission.short
  const plain = mission.name.replace(/^U\.S\. /, '')
  return plain.length <= 14 ? plain : mission.short
}

export function themeFor(mission: Mission): ThemePair {
  return mission.id === america.id ? americaTheme : deriveTheme(mission.palette)
}
